import { useCallback, useMemo, useState, type CSSProperties } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../../hooks/useApi';
import { listTournamentSeries, type TournamentSeriesActiveFilter } from '../../lib/api';
import { DataTable } from '../../components/DataTable';
import { PS, T } from '../../lib/theme';
import { formatClock } from '../../lib/time';
import { feeGelFromMinor } from '../../lib/tournaments';
import {
  tournamentSeriesRecurrenceLabel,
  tournamentSeriesStatusColor,
  tournamentSeriesStatusLabel,
} from '../../lib/tournament-series';
import { formatGel } from '../../lib/format';
import type { AdminTournamentSeries } from '../../lib/types';
import { compareDateDescending, orderByDateDescending } from '../../lib/admin-list-order';

const actionBase: CSSProperties = {
  padding: '8px 16px',
  borderRadius: 3,
  minHeight: 40,
  display: 'inline-flex',
  alignItems: 'center',
  ...T.buttonLabel,
  textTransform: 'uppercase',
  textDecoration: 'none',
};

const primaryAction: CSSProperties = { ...actionBase, background: PS.red, color: PS.white };
const secondaryAction: CSSProperties = {
  ...actionBase,
  background: PS.white,
  color: PS.nearBlack,
  border: `1px solid ${PS.border}`,
};

export function TournamentSeriesPage() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<TournamentSeriesActiveFilter>('true');

  const loader = useCallback(
    (signal: AbortSignal) => listTournamentSeries(activeFilter, signal),
    [activeFilter],
  );
  const series = useApi<AdminTournamentSeries[]>(loader);
  const orderedSeries = useMemo(
    () => orderByDateDescending(series.data ?? [], (item) => ({ date: item.starts_on, id: item.id })),
    [series.data],
  );

  const columns = useMemo<ColumnDef<AdminTournamentSeries, unknown>[]>(
    () => [
      { header: 'ID', accessorKey: 'id', cell: (info) => `#${info.getValue<number>()}` },
      {
        header: 'Title',
        // `en` first — same reasoning as `TournamentsPage`: the admin UI
        // itself is English-only, not a claim about locale priority.
        accessorFn: (row) => row.translations.en?.title ?? row.title,
        id: 'title',
      },
      {
        header: 'Cadence',
        accessorFn: (row) => tournamentSeriesRecurrenceLabel(row.day_of_week),
        id: 'cadence',
      },
      {
        header: 'Time',
        accessorFn: (row) => `${formatClock(row.start_time)}–${formatClock(row.end_time)}`,
        id: 'time',
      },
      {
        header: 'Status',
        id: 'status',
        cell: (info) => <StatusBadge status={info.row.original.status} />,
      },
      {
        header: 'Fee',
        accessorFn: (row) => formatGel(feeGelFromMinor(row.fee_minor)),
        id: 'fee',
      },
      {
        header: 'Starts',
        accessorKey: 'starts_on',
        sortingFn: (left, right) => -compareDateDescending(
          { date: left.original.starts_on, id: left.original.id },
          { date: right.original.starts_on, id: right.original.id },
        ),
        sortUndefined: 'last',
      },
      {
        header: 'Ends',
        accessorFn: (row) => row.ends_on ?? '— (open)',
        id: 'ends',
      },
    ],
    [],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Tournament series</h1>
        <Link to="/tournaments/series/new" style={primaryAction}>
          New series
        </Link>
        <Link to="/tournaments" style={secondaryAction}>
          ← One-off tournaments
        </Link>
      </header>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as TournamentSeriesActiveFilter)}
            style={{
              padding: '8px 10px',
              border: `1px solid ${PS.border}`,
              borderRadius: 3,
              minHeight: 40,
              ...T.ui,
              background: PS.white,
            }}
          >
            <option value="true">Active</option>
            <option value="false">Ended</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={() => void series.refetch()}
            style={{
              background: PS.white,
              border: `1px solid ${PS.border}`,
              padding: '8px 12px',
              borderRadius: 3,
              cursor: 'pointer',
              minHeight: 40,
              ...T.navLink,
              textTransform: 'uppercase' as const,
            }}
          >
            Refresh
          </button>
        </header>

        {series.loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}

        {series.error && (
          <div
            style={{
              padding: 12,
              background: PS.errorBg,
              border: `1px solid ${PS.errorBorder}`,
              color: PS.errorText,
              borderRadius: 3,
            }}
          >
            {series.error.message}
          </div>
        )}

        {!series.loading && !series.error && (
          <DataTable
            columns={columns}
            data={orderedSeries}
            emptyLabel="No tournament series yet."
            initialSort={[{ id: 'starts_on', desc: true }]}
            onRowClick={(row) => navigate(`/tournaments/series/${row.id}`)}
            getRowLabel={(row) => `Open tournament series ${row.id}`}
          />
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: AdminTournamentSeries['status'] }) {
  const color = tournamentSeriesStatusColor(status);
  return (
    <span
      style={{
        ...T.microLabel,
        color,
        background: PS.white,
        border: `1px solid ${color}`,
        borderRadius: 3,
        padding: '2px 8px',
      }}
    >
      {tournamentSeriesStatusLabel(status)}
    </span>
  );
}
